import { call, delay, put, race, take, takeLatest } from 'redux-saga/effects';
import columns from 'utils/columns/sites';
import { initialStateFactory, reducerFactory, sagasFactory } from 'utils/factories';
import actionsFactory from 'utils/factories/actions';
import { apiRequest, uploadFiles } from 'utils/request';
import { handleError } from 'utils/sagasHelpers';
import { FETCH_SITES, FULFILLED, LOAD_SITE, REFRESH_SITE_STATUS, REJECTED, SITE, UPLOAD_BRIDGE_DEVICES, UPLOAD_DEVICES, UPLOAD_DIGITAL_DEVICES } from './actionTypes';

const START_POOLING_SITES = 'START_POOLING_SITES';
const STOP_POOLING_SITES = 'STOP_POOLING_SITES';

// actions
const actions = actionsFactory(SITE);

actions.refreshStatus = id => ({
    payload: id,
    type: REFRESH_SITE_STATUS
});

actions.startPoolingSites = () => ({
    type: START_POOLING_SITES
});

actions.stopPoolingSites = () => ({
    type: STOP_POOLING_SITES
});

actions.uploadDevices = (id, file) => ({
    payload: { id, file },
    type: UPLOAD_DEVICES
});

actions.uploadBridgeDevices = (id, file) => ({
    payload: { id, file },
    type: UPLOAD_BRIDGE_DEVICES
});

actions.uploadDigitalDevices = (id, file) => ({
    payload: { id, file },
    type: UPLOAD_DIGITAL_DEVICES
});

export { actions };

// reducer
const initialState = { ...initialStateFactory(columns), uploadingDevices: false };

const reducer = reducerFactory(SITE);

export default (state = initialState, action) => {
    const { type } = action;

    switch (type) {
        case REFRESH_SITE_STATUS:
            return reducer({ ...state, submitting: true }, action);

        case REFRESH_SITE_STATUS + FULFILLED:
        case REFRESH_SITE_STATUS + REJECTED:
            return reducer({ ...state, submitting: false }, action);

        case UPLOAD_DEVICES:
        case UPLOAD_BRIDGE_DEVICES:
        case UPLOAD_DIGITAL_DEVICES:
            return reducer({ ...state, uploadingDevices: true }, action);

        case UPLOAD_DEVICES + FULFILLED:
        case UPLOAD_BRIDGE_DEVICES + FULFILLED:
        case UPLOAD_DIGITAL_DEVICES + FULFILLED:
        case UPLOAD_DEVICES + REJECTED:
        case UPLOAD_BRIDGE_DEVICES + REJECTED:
        case UPLOAD_DIGITAL_DEVICES + REJECTED:
            return reducer({ ...state, uploadingDevices: false }, action);

        default:
            return reducer(state, action);
    }
};

// sagas
const sagas = sagasFactory(SITE, '/sites', { searchInputDelay: 500 });

const POOL_INTERVAL = 60 * 1000;

function* poolSites() {
    try {
        while (true) {
            yield put({ type: FETCH_SITES });
            yield delay(POOL_INTERVAL);
        }
    } catch (error) {
        console.error(error);
    }
}

function* watchSitesPooling() {
    try {
        while (true) {
            yield take(START_POOLING_SITES);
            yield race({
                task: call(poolSites),
                cancel: take(STOP_POOLING_SITES)
            });
        }
    } catch (error) {
        console.error(error);
    }
}

function* watchRefreshSiteStatus() {
    yield takeLatest(REFRESH_SITE_STATUS, function* ({ payload: id }) {
        try {
            yield apiRequest(`/sites/${id}/refresh_status`, 'PUT');
            yield put({ type: REFRESH_SITE_STATUS + FULFILLED });
            yield put({ type: LOAD_SITE, payload: id });
        } catch (error) {
            const errorObject = handleError(REFRESH_SITE_STATUS, error);
            yield put(errorObject);
        }
    });
}

function* watchUploadBridgeDevices() {
    yield takeLatest(UPLOAD_BRIDGE_DEVICES, function* ({ payload: { id, file } }) {
        try {
            yield uploadFiles(`/sites/${id}/upload_bridge_devices`, 'POST', file);
            yield put({ type: UPLOAD_BRIDGE_DEVICES + FULFILLED });
            yield put({ type: LOAD_SITE, payload: id });
        } catch (error) {
            const errorObject = handleError(UPLOAD_BRIDGE_DEVICES, error);
            yield put(errorObject);
        }
    });
}

function* watchUploadDevices() {
    yield takeLatest(UPLOAD_DEVICES, function* ({ payload: { id, file } }) {
        try {
            yield uploadFiles(`/sites/${id}/upload_devices`, 'POST', file);
            yield put({ type: UPLOAD_DEVICES + FULFILLED });
            yield put({ type: LOAD_SITE, payload: id });
        } catch (error) {
            const errorObject = handleError(UPLOAD_DEVICES, error);
            yield put(errorObject);
        }
    });
}

function* watchUploadDigitalDevices() {
    yield takeLatest(UPLOAD_DIGITAL_DEVICES, function* ({ payload: { id, file } }) {
        try {
            yield uploadFiles(`/sites/${id}/upload_digital_devices`, 'POST', file);
            yield put({ type: UPLOAD_DIGITAL_DEVICES + FULFILLED });
            yield put({ type: LOAD_SITE, payload: id });
        } catch (error) {
            const errorObject = handleError(UPLOAD_DIGITAL_DEVICES, error);
            yield put(errorObject);
        }
    });
}

sagas.push(
    watchSitesPooling,
    watchRefreshSiteStatus,
    watchUploadBridgeDevices,
    watchUploadDevices,
    watchUploadDigitalDevices
);

export { sagas };
